import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Stack,
  Text,
} from "@chakra-ui/react";
import { LatLngLiteral } from "leaflet";
import { MapPinned } from "lucide-react";
import { InvalidGeofenceAlert } from "pages/ForecastChart/ForecastChartPanelMap/InvalidGeofenceAlert";
import React from "react";
import { Select } from "antd";
import { ILocation } from "pages/Location/types";
import { GeofenceMapSelector } from "./GeofenceMapSelector";
import { BackButton } from "./BackButton";
import { useGeofenceMapContainer } from "./hooks/useGeofenceMapContainer";

interface FormErrors {
  geofence?: string;
  [key: string]: any;
}

interface FormTouched {
  geofence?: boolean;
  [key: string]: any;
}

interface GeofenceMapContainerProps {
  values: any;
  errors: FormErrors;
  touched: FormTouched;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  selectedGeofence: LatLngLiteral[];
  handleDrawingMode: () => void;
  handleGeoJsonLoaded: (geojson: any) => void;
  t: (key: string) => string;
  showGeofenceError?: boolean;
  clearGeofence: () => void;
  setIsGeofenceLoadedByGeoJson: (value: boolean) => void;
  areas: ILocation[];
  handleAreaSelection?: (
    selectedAreaId: number,
    areas: ILocation[],
    setFieldValue: (field: string, value: any) => void,
  ) => void;
  handleEnableAreaSelector?: (value: boolean) => void;
  areaSelectorVisible?: boolean;
  hideSaveAreaButtonOnMap?: boolean;
  onBackToTypeSelection?: () => void;
  keepGeofenceAfterSave?: boolean;
  isLocationLocked?: boolean;
}

export const GeofenceMapContainer: React.FC<GeofenceMapContainerProps> = ({
  values,
  errors,
  touched,
  selectedGeofence,
  handleDrawingMode,
  handleGeoJsonLoaded,
  setFieldValue,
  t,
  showGeofenceError,
  clearGeofence,
  setIsGeofenceLoadedByGeoJson,
  areas,
  handleAreaSelection,
  handleEnableAreaSelector,
  areaSelectorVisible,
  hideSaveAreaButtonOnMap,
  onBackToTypeSelection,
  keepGeofenceAfterSave = false,
  isLocationLocked = false,
}) => {
  const {
    isDropZoneVisible,
    setIsDropZoneVisible,
    showBackButton,
    handleBack,
    hasGeofenceError,
  } = useGeofenceMapContainer({
    areaSelectorVisible,
    selectedGeofence,
    clearGeofence,
    handleEnableAreaSelector,
    errors,
    touched,
    onBackToTypeSelection,
  });
  return (
    <Stack spacing={6}>
      <Box borderWidth="1px" borderRadius="md" p={4}>
        <Flex gap="12px" mb="16px" justifyContent={"space-between"}>
          <Flex gap="12px">
            <MapPinned color="#086AA9" size="20px" />
            <Text fontWeight={"400"} fontSize="md" color="#086AA9">
              {t("monitoredLocation")}
            </Text>
          </Flex>
          {!isLocationLocked && (
            <BackButton
              onClick={() => {
                handleBack();
                setFieldValue("locationId", undefined);
              }}
              isVisible={showBackButton}
              label={t("back") || "Voltar"}
            />
          )}
        </Flex>

        {showGeofenceError && (
          <Box mb={2}>
            <InvalidGeofenceAlert t={t} />
          </Box>
        )}

        {/* Show area selector dropdown when areaSelectorVisible is true and not locked */}
        {areaSelectorVisible && !isLocationLocked && (
          <FormControl isRequired mb={4}>
            <FormLabel color="#414651" fontWeight={"500"}>
              {t("area")}
            </FormLabel>
            <Box>
              <Select
                id="select-station"
                showSearch
                optionFilterProp="children"
                placeholder={t("selectAreaPlaceholder") as string}
                onChange={(selectedAreaId: number) => {
                  if (handleAreaSelection) {
                    handleAreaSelection(selectedAreaId, areas, setFieldValue);
                  }
                }}
                value={values.locationId}
                style={{
                  width: "100%",
                  height: "40px",
                  borderColor: "#eff0f3",
                }}
                dropdownStyle={{ zIndex: 2000 }}
              >
                {areas.map((area) => (
                  <Select.Option value={area.id} key={area.id}>
                    {area.name}
                  </Select.Option>
                ))}
              </Select>
            </Box>
            <FormErrorMessage mt={4}>{t("thisFieldRequired")}</FormErrorMessage>
          </FormControl>
        )}

        {/* Show selected saved area in read-only mode when location is locked and has a locationId */}
        {isLocationLocked && values.locationId && (
          <FormControl mb={4}>
            <FormLabel color="#414651" fontWeight={"500"}>
              {t("area")}
            </FormLabel>
            <Box>
              <Select
                id="select-station-readonly"
                disabled
                value={values.locationId}
                style={{
                  width: "100%",
                  height: "40px",
                  borderColor: "#eff0f3",
                }}
              >
                {areas.map((area) => (
                  <Select.Option value={area.id} key={area.id}>
                    {area.name}
                  </Select.Option>
                ))}
              </Select>
            </Box>
          </FormControl>
        )}
        <FormControl isRequired isInvalid={hasGeofenceError}>
          <GeofenceMapSelector
            coordinates={selectedGeofence}
            selectedGeofence={selectedGeofence}
            t={t}
            handleDrawingMode={isLocationLocked ? undefined : handleDrawingMode}
            handleGeoJsonLoaded={
              isLocationLocked ? undefined : handleGeoJsonLoaded
            }
            clearGeofence={isLocationLocked ? undefined : clearGeofence}
            setIsGeofenceLoadedByGeoJson={setIsGeofenceLoadedByGeoJson}
            handleEnableAreaSelector={
              isLocationLocked ? undefined : handleEnableAreaSelector
            }
            areaSelectorVisible={areaSelectorVisible}
            hideSaveAreaButtonOnMap={
              hideSaveAreaButtonOnMap || isLocationLocked
            }
            isGeofenceInvalid={showGeofenceError}
            isDropZoneVisible={isLocationLocked ? false : isDropZoneVisible}
            setIsDropZoneVisible={
              isLocationLocked ? undefined : setIsDropZoneVisible
            }
            setFieldValue={
              keepGeofenceAfterSave || isLocationLocked
                ? undefined
                : setFieldValue
            }
            isLocationLocked={isLocationLocked}
          />

          <FormErrorMessage mt={4}>{t("pleaseDrawAnArea")}</FormErrorMessage>
        </FormControl>
      </Box>
    </Stack>
  );
};
